/**
 * Auth Lifecycle Setup
 *
 * React component that registers authentication lifecycle handlers.
 * Uses dependency injection through AuthContext.
 *
 * This approach uses dependency inversion:
 * - AuthContext provides the registration mechanism
 * - This component injects the specific handlers
 * - Handlers use managers instead of direct localStorage access
 *
 * Cache Management:
 * - Login handler: Fetches and populates user cache from backend
 * - Logout handler: Clears all user-related cache data
 *
 * Preferences Flow:
 * - On sign-up: anonymous preferences are synced to the new account
 * - On login: backend preferences take priority (no merge)
 */

import { useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { userManager } from '@/managers/userManager'
import { userService } from '@/services/userService'
import { analyticsService } from '@/lib/firebase'

/**
 * Handler to populate cache after login/signup
 * For sign-up: syncs anonymous preferences to the new account
 * For login: uses backend preferences as-is
 */
async function populateUserCacheHandler(user?: any): Promise<void> {
  try {
    if (!user) {
      console.warn('[AuthLifecycle] No user data provided, fetching from backend')
      await userManager.fetchFresh()
      return
    }

    // Capture anonymous preferences BEFORE clearing cache
    const wasAnonymous = userManager.isAnonymous()
    const anonymousPrefs = wasAnonymous ? userManager.getPreferences() : null

    // Clear any existing cache (including anonymous user)
    userManager.clear()

    // Save authenticated user to cache
    userManager.set(user)

    // If user was anonymous and had preferences, and the new account has no preferences,
    // sync anonymous preferences to the backend (sign-up flow)
    const userHasNoPrefs = !user.preferences?.modalities?.length
    const anonHasPrefs = anonymousPrefs?.modalities && anonymousPrefs.modalities.length > 0

    if (wasAnonymous && anonHasPrefs && userHasNoPrefs) {
      try {
        await userService.saveUserPreferences(anonymousPrefs)
        // Update cache with the synced preferences
        userManager.savePreferences(anonymousPrefs)
      } catch (error) {
        console.error('[AuthLifecycle] Failed to sync anonymous preferences:', error)
      }
    }
  } catch (error) {
    console.error('[AuthLifecycle] Failed to populate user cache:', error)
  }
}

/**
 * Handler to clear all user data on logout
 * Clears authenticated user and creates anonymous user
 */
function clearAllUserDataHandler(): void {
  userManager.clear()
  userManager.setAnonymous({ modalities: [] })
}

/**
 * Component that sets up authentication lifecycle handlers
 * Must be rendered inside AuthProvider
 */
export function AuthLifecycleSetup() {
  const { registerLoginHandler, registerLogoutHandler } = useAuth()

  useEffect(() => {
    const unregisterLogout = registerLogoutHandler(() => {
      // Clear analytics identity so events aren't attributed to the previous user.
      analyticsService.setUserId(null)
      clearAllUserDataHandler()
    })

    const unregisterLogin = registerLoginHandler(async (user) => {
      // Identify the user for analytics (internal id — pseudonymous, non-PII).
      if (user?.id) {
        analyticsService.setUserId(user.id)
        analyticsService.setUserProperties({
          has_favorite_modalities: !!user.preferences?.modalities?.length,
          favorite_modalities_count: user.preferences?.modalities?.length ?? 0,
        })
      }
      await populateUserCacheHandler(user)
    })

    return () => {
      unregisterLogout()
      unregisterLogin()
    }
  }, [registerLoginHandler, registerLogoutHandler])

  return null
}
